/**
 * HollyContext — shared state for all Holly components
 *
 * Provides: messages, threadId, isLoading, isChatOpen, and actions to send /
 * seed messages and open/close the chat panel.
 *
 * Adapted for the Flox app (Vite + React) from flox-website-5.0:
 *  - `mapboxToken` is injected as a prop (the `MAPBOX_APIKEY` constant) instead
 *    of being fetched from `/api/holly/config`.
 *  - `controlledOpen` / `onRequestClose` let a parent (the registration page)
 *    own the open/closed state.
 *  - `onReadyToRegister` is the in-page registration handoff.
 *  - `seedMessage` sends without rendering a user bubble.
 *  - thread persistence uses localStorage `holly_thread_id` (read by
 *    `useHollySession`), and there is NO restore-from-storage on mount so each
 *    journey starts a fresh thread.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  type ReactNode,
} from 'react';
import { askHolly } from './hollyService';
import type {
  HollyApiResponse,
  HollyContextValue,
  HollyMessage,
  HollySuggestedCta,
  HollyWarehouse,
} from './types';

const HollyContext = createContext<HollyContextValue | null>(null);

/* ── Static onboarding chips ─────────────────────────────────────────
 * Rendered below the welcome card when no conversation has started yet.
 * Once the user sends a message, server-driven suggestedCtas take over. */
export const HOLLY_WELCOME_CHIPS: HollySuggestedCta[] = [
  { message: 'Find a warehouse', needsUserInput: true, isHtml: false },
  { message: 'Get a transport quote', needsUserInput: true, isHtml: false },
  { message: 'Search providers', needsUserInput: true, isHtml: false },
];

/* ── localStorage key for thread persistence (read by useHollySession) ── */
const THREAD_KEY = 'holly_thread_id';

function storeThreadId(id: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(THREAD_KEY, id);
  } catch {
    /* localStorage may be unavailable in some contexts */
  }
}

/* ── Unique ID generator ── */
let idCounter = 0;
function uniqueId(): string {
  idCounter += 1;
  return `holly-${Date.now()}-${idCounter}`;
}

/* ── Provider ── */

interface HollyProviderProps {
  children: ReactNode;
  /** Mapbox public token (pk.*) — the Flox `MAPBOX_APIKEY` constant. */
  mapboxToken: string;
  /** When defined, the parent owns the open/closed state (registration). */
  controlledOpen?: boolean;
  /** Called when the panel closes via ×/backdrop/Escape (controlled mode). */
  onRequestClose?: () => void;
  /** In-page registration handoff: fired with the current threadId. */
  onReadyToRegister?: (threadId: string) => void;
  /** Sent on the first orchestrate call only (gateway currently drops it). */
  declaredIntent?: string | null;
  /** When false, the static welcome chips are suppressed (controlled mode). */
  enableWelcomeChips?: boolean;
  onTrackEvent?: (event: string, data?: Record<string, unknown>) => void;
}

export function HollyProvider({
  children,
  mapboxToken,
  controlledOpen,
  onRequestClose,
  onReadyToRegister,
  declaredIntent = null,
  enableWelcomeChips = true,
  onTrackEvent,
}: HollyProviderProps) {
  const [messages, setMessages] = useState<HollyMessage[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] =
    useState<HollyWarehouse | null>(null);
  const [selectedWarehouseLabel, setSelectedWarehouseLabel] = useState<
    string | null
  >(null);

  /* declaredIntent is consumed once on the first orchestrate call. */
  const declaredIntentRef = useRef<string | null>(declaredIntent);

  /* When the parent controls the drawer, mirror its open prop. onRequestClose
   * is intentionally NOT called here — only a user-initiated close calls it,
   * so this sync never loops. */
  useEffect(() => {
    if (controlledOpen === undefined) return;
    setIsChatOpen(!!controlledOpen);
  }, [controlledOpen]);

  /* Ref to track the button/element that opened the chat (for focus return) */
  const triggerRef = useRef<HTMLElement | null>(null);

  const openChat = useCallback(() => {
    triggerRef.current =
      typeof document !== 'undefined'
        ? (document.activeElement as HTMLElement | null)
        : null;
    setIsChatOpen(true);
    onTrackEvent?.('holly_chat_open', {
      trigger_page:
        typeof window !== 'undefined' ? window.location.pathname : '',
    });
  }, [onTrackEvent]);

  const closeChat = useCallback(() => {
    setIsChatOpen(false);
    setSelectedWarehouse(null);
    setSelectedWarehouseLabel(null);
    /* Return focus to the element that triggered open */
    requestAnimationFrame(() => {
      triggerRef.current?.focus();
    });
    /* Notify a controlling parent so it can flip its own state. */
    onRequestClose?.();
  }, [onRequestClose]);

  const selectWarehouse = useCallback(
    (warehouse: HollyWarehouse | null, label?: string | null) => {
      setSelectedWarehouse(warehouse);
      setSelectedWarehouseLabel(label ?? null);
      if (warehouse) {
        onTrackEvent?.('holly_warehouse_view', {
          warehouse_id: warehouse.id,
          label: label ?? null,
        });
      }
    },
    [onTrackEvent]
  );

  const toggleChat = useCallback(() => {
    if (isChatOpen) {
      closeChat();
    } else {
      openChat();
    }
  }, [isChatOpen, openChat, closeChat]);

  /* Shared response handling for both sendMessage and seedMessage. */
  const applyHollyResponse = useCallback((response: HollyApiResponse) => {
    if (response.threadId) {
      setThreadId(response.threadId);
      storeThreadId(response.threadId);
    }

    const warehouses = response.warehouses ?? [];
    const ctas = response.suggestedCtas ?? [];
    const quotes = response.transportationQuotes ?? [];
    const hollyContent = (response.messages ?? []).join('\n\n');

    const hollyMsg: HollyMessage = {
      id: uniqueId(),
      role: 'holly',
      content: hollyContent,
      /* When the backend split the agent's message around a blank line
       * (transport-quote replies), surface the lead-in here so the message
       * bubble can render it ABOVE the card. */
      preCardContent:
        typeof response.preCardMessage === 'string' &&
        response.preCardMessage.trim().length > 0
          ? response.preCardMessage
          : undefined,
      warehouses: warehouses.length > 0 ? warehouses : undefined,
      transportationQuotes: quotes.length > 0 ? quotes : undefined,
      suggestedCtas: ctas.length > 0 ? ctas : undefined,
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, hollyMsg]);
  }, []);

  const appendErrorMessage = useCallback(() => {
    const errorMsg: HollyMessage = {
      id: uniqueId(),
      role: 'holly',
      content:
        "Sorry, I'm having trouble connecting right now. Please try again.",
      suggestedCtas: [
        { message: 'Try again', needsUserInput: false, isHtml: false },
      ],
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, errorMsg]);
  }, []);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading) return;

      /* Add user message */
      const userMsg: HollyMessage = {
        id: uniqueId(),
        role: 'user',
        content: trimmed,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setIsLoading(true);

      onTrackEvent?.('holly_chat_message', {
        message_length: trimmed.length,
        is_quick_reply: false,
      });

      try {
        const intent = declaredIntentRef.current;
        const response = await askHolly(trimmed, threadId, intent);
        declaredIntentRef.current = null;
        applyHollyResponse(response);
      } catch {
        appendErrorMessage();
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, threadId, onTrackEvent, applyHollyResponse, appendErrorMessage]
  );

  /* Like sendMessage but renders NO user bubble — silent seed so Holly opens
   * straight onto her first question (registration initialMessage / the
   * /embed AIInput directive). */
  const seedMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading) return;
      setIsLoading(true);
      try {
        const intent = declaredIntentRef.current;
        const response = await askHolly(trimmed, threadId, intent);
        declaredIntentRef.current = null;
        applyHollyResponse(response);
      } catch {
        appendErrorMessage();
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, threadId, applyHollyResponse, appendErrorMessage]
  );

  const value = useMemo<HollyContextValue>(
    () => ({
      messages,
      threadId,
      isLoading,
      isChatOpen,
      selectedWarehouse,
      selectedWarehouseLabel,
      openChat,
      closeChat,
      toggleChat,
      sendMessage,
      seedMessage,
      selectWarehouse,
      onReadyToRegister,
      welcomeChipsEnabled: enableWelcomeChips,
      onTrackEvent,
      mapboxToken,
    }),
    [
      messages,
      threadId,
      isLoading,
      isChatOpen,
      selectedWarehouse,
      selectedWarehouseLabel,
      openChat,
      closeChat,
      toggleChat,
      sendMessage,
      seedMessage,
      selectWarehouse,
      onReadyToRegister,
      enableWelcomeChips,
      onTrackEvent,
      mapboxToken,
    ]
  );

  return (
    <HollyContext.Provider value={value}>{children}</HollyContext.Provider>
  );
}

/* ── Hook ── */

export function useHolly(): HollyContextValue {
  const ctx = useContext(HollyContext);
  if (!ctx) {
    throw new Error('useHolly must be used within a HollyProvider');
  }
  return ctx;
}
